import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuditTemplate from '../components/AuditTemplate';
import { auditsApi } from '../services/api';

const AuditTemplatePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const auditId = searchParams.get('auditId');
  const mode = searchParams.get('mode'); // 'supervisor' o 'operador'
  const operatorId = searchParams.get('operatorId');
  const returnTo = searchParams.get('returnTo');

  const [auditData, setAuditData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!auditId) {
      setError('ID de auditoría no especificado');
      setLoading(false);
      return;
    }

    loadAuditData();
  }, [auditId, mode]);

  const loadAuditData = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      
      if (mode === 'supervisor') {
        // Cargar datos específicos para supervisor (usando el endpoint que creamos)
        response = await auditsApi.getSupervisorFormData(parseInt(auditId!));
      } else {
        // Cargar datos normales para operador
        response = await auditsApi.getById(parseInt(auditId!));
      }

      
      if (response.data.success) {
        const data = response.data.data;
        // console.log('📋 Datos cargados:', {
        //   auditId: data.auditoria?.id,
        //   titulo: data.auditoria?.titulo,
        //   eventosCount: data.eventos?.length || 0,
        //   isSupervisorMode: mode === 'supervisor'
        // });
        setAuditData(data);
      } else {
        setError('Error al cargar los datos de la auditoría');
      }
    } catch (error) {
      console.error('Error cargando datos de auditoría:', error);
      setError('Error al cargar los datos de la auditoría');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Cerrar la ventana o redirigir según el contexto
    if (window.opener) {
      // Si se abrió en nueva ventana, cerrarla
      window.close();
    } else {
      // Si no, redirigir al dashboard o al returnTo
      if (returnTo === 'supervisor-review') {
        navigate('/dashboard?tab=supervisor');
      } else {
        navigate('/dashboard');
      }
    }
  };

  const handleComplete = async (formData: any) => {
    try {
      if (mode === 'supervisor') {
        // console.log('📋 SUPERVISOR - Enviando datos:', {
        //   auditId: parseInt(auditId!),
        //   observacionesFinales: formData.observacionesFinales,
        //   eventosCount: formData.eventos?.length || 0,
        //   eventos: formData.eventos,
        //   estado: formData.estado
        // });
        
        if (formData.estado === 'aprobada') {
          // Aprobar y enviar al cliente
          await auditsApi.supervisorApprove(parseInt(auditId!), {
            observacionesSupervisor: formData.observacionesFinales || '',
            observacionesFinales: formData.observacionesFinales || '',
            eventosEditados: formData.eventos || []
          });
          
          alert('✅ Auditoría aprobada y enviada al cliente exitosamente.');
          handleClose();
        } else if (formData.estado === 'guardado_supervisor') {
          // Solo guardar cambios sin enviar al cliente
          const dataToSend = {
            eventos: formData.eventos,
            observacionesFinales: formData.observacionesFinales,
            tiempoTrabajado: formData.tiempoTrabajado,
            sucursalDetails: formData.sucursalDetails,
            canalesVision: formData.canalesVision,
            estado: 'terminada' // Mantener en terminada para revisión
          };
          
          
          await auditsApi.complete(parseInt(auditId!), dataToSend);
          
          alert('💾 Cambios guardados exitosamente. La auditoría queda lista para aprobar.');
          // NO cerrar - permitir seguir editando
        } else {
          // Modo supervisor pero con estado normal (fallback)
          await auditsApi.complete(parseInt(auditId!), formData);
          alert('Cambios guardados exitosamente.');
          handleClose();
        }
      } else {
        // Completar auditoría normal (operador)
        await auditsApi.complete(parseInt(auditId!), formData);

        alert('Auditoría completada exitosamente.');
        handleClose();
      }
    } catch (error) {
      console.error('Error al guardar cambios:', error);
      alert('Error al guardar los cambios');
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <div className="spinner" />
        <p style={{ marginTop: '1rem' }}>
          {mode === 'supervisor' ? 'Cargando formulario para supervisión...' : 'Cargando auditoría...'}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <h2 style={{ color: '#ef4444' }}>Error</h2>
        <p>{error}</p>
        <button 
          onClick={handleClose}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            marginTop: '1rem'
          }}
        >
          Cerrar
        </button>
      </div>
    );
  }

  if (!auditData) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>No se encontraron datos de la auditoría</p>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', overflow: 'hidden' }}>
      {mode === 'supervisor' && (
        <div style={{
          backgroundColor: '#1e40af',
          color: 'white',
          padding: '10px 20px',
          textAlign: 'center',
          fontSize: '0.9rem',
          fontWeight: '600'
        }}>
          🔧 MODO SUPERVISIÓN - Editando planilla del operador {auditData.meta?.operadorNombre}
        </div>
      )}
      
      <AuditTemplate
        auditId={parseInt(auditId!)}
        onClose={handleClose}
        onComplete={handleComplete}
        user={user}
        initialData={auditData}
        supervisorMode={mode === 'supervisor' && auditData?.auditoria?.operadorId !== user?.id}
      />
    </div>
  );
};

export default AuditTemplatePage;